import React from 'react';
import {
    Box,
    Typography,
    TextField,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from '@mui/material';
import {
    Info as InfoIcon,
    ExpandMore as ExpandIcon,
    Code as CodeIcon
} from '@mui/icons-material';

interface XAPIContent {
    activityId?: string;
    activityType?: string;
    activityName?: string;
    activityDescription?: string;
    launchUrl?: string;
    lrsEndpoint?: string;
    lrsUsername?: string;
    lrsPassword?: string;
    registrationId?: string;
    completionVerb?: string;
    successVerb?: string;
    trackingMode?: 'statements' | 'state' | 'both';
    requireCompletion?: boolean;
    requireSuccess?: boolean;
    minScore?: number;
    maxScore?: number;
    sendActor?: boolean;
    sendContext?: boolean;
}

interface XAPIEditorProps {
    content: XAPIContent;
    onChange: (content: XAPIContent) => void;
}

const activityTypes = [
    { value: 'http://adlnet.gov/expapi/activities/course', label: 'Course' },
    { value: 'http://adlnet.gov/expapi/activities/module', label: 'Module' },
    { value: 'http://adlnet.gov/expapi/activities/lesson', label: 'Lesson' },
    { value: 'http://adlnet.gov/expapi/activities/assessment', label: 'Assessment' },
    { value: 'http://adlnet.gov/expapi/activities/simulation', label: 'Simulation' },
    { value: 'http://adlnet.gov/expapi/activities/interaction', label: 'Interaction' },
    { value: 'http://adlnet.gov/expapi/activities/media', label: 'Media' },
    { value: 'http://adlnet.gov/expapi/activities/file', label: 'File' },
    { value: 'http://adlnet.gov/expapi/activities/link', label: 'Link' },
    { value: 'http://adlnet.gov/expapi/activities/question', label: 'Question' }
];

const xapiVerbs = [
    { value: 'http://adlnet.gov/expapi/verbs/completed', label: 'completed' },
    { value: 'http://adlnet.gov/expapi/verbs/passed', label: 'passed' },
    { value: 'http://adlnet.gov/expapi/verbs/failed', label: 'failed' },
    { value: 'http://adlnet.gov/expapi/verbs/answered', label: 'answered' },
    { value: 'http://adlnet.gov/expapi/verbs/attempted', label: 'attempted' },
    { value: 'http://adlnet.gov/expapi/verbs/experienced', label: 'experienced' },
    { value: 'http://adlnet.gov/expapi/verbs/mastered', label: 'mastered' },
    { value: 'http://adlnet.gov/expapi/verbs/scored', label: 'scored' }
];

const XAPIEditor: React.FC<XAPIEditorProps> = ({ content, onChange }) => {
    return (
        <Box>
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                    xAPI (Experience API / Tin Can API)
                </Typography>
                <Typography variant="body2">
                    xAPI ermöglicht die Verfolgung und Speicherung von Lernerfahrungen in einem Learning Record Store (LRS).
                    Definieren Sie hier die xAPI-Aktivität und konfigurieren Sie die LRS-Verbindung.
                </Typography>
            </Alert>

            <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                    <Typography variant="h6">Aktivitätsdefinition</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Activity ID (IRI)"
                            fullWidth
                            required
                            value={content.activityId || ''}
                            onChange={(e) => onChange({ ...content, activityId: e.target.value })}
                            placeholder="https://example.com/activities/lesson-1"
                            helperText="Eindeutige IRI (Internationalized Resource Identifier) für diese Aktivität"
                        />

                        <FormControl fullWidth required>
                            <InputLabel>Activity Type</InputLabel>
                            <Select
                                value={content.activityType || ''}
                                label="Activity Type"
                                onChange={(e) => onChange({ ...content, activityType: e.target.value })}
                            >
                                {activityTypes.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Activity Name"
                            fullWidth
                            required
                            value={content.activityName || ''}
                            onChange={(e) => onChange({ ...content, activityName: e.target.value })}
                            helperText="Anzeigename der Aktivität"
                        />

                        <TextField
                            label="Activity Description"
                            fullWidth
                            multiline
                            rows={3}
                            value={content.activityDescription || ''}
                            onChange={(e) => onChange({ ...content, activityDescription: e.target.value })}
                            helperText="Detaillierte Beschreibung der Aktivität"
                        />

                        <TextField
                            label="Launch URL"
                            fullWidth
                            value={content.launchUrl || ''}
                            onChange={(e) => onChange({ ...content, launchUrl: e.target.value })}
                            placeholder="https://example.com/content/launch"
                            helperText="URL zum Starten der xAPI-Aktivität (optional, falls extern gehostet)"
                        />
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Accordion>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6">LRS-Konfiguration</Typography>
                        <Chip label="Learning Record Store" size="small" variant="outlined" />
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                LRS-Zugangsdaten werden verschlüsselt gespeichert. Standardmäßig wird der zentrale
                                LRS des Systems verwendet, sofern nicht anders konfiguriert.
                            </Typography>
                        </Alert>

                        <TextField
                            label="LRS Endpoint URL"
                            fullWidth
                            value={content.lrsEndpoint || ''}
                            onChange={(e) => onChange({ ...content, lrsEndpoint: e.target.value })}
                            placeholder="https://lrs.example.com/xapi/"
                            helperText="Leer lassen, um den Standard-LRS zu verwenden"
                        />

                        <TextField
                            label="LRS Username / Key"
                            fullWidth
                            value={content.lrsUsername || ''}
                            onChange={(e) => onChange({ ...content, lrsUsername: e.target.value })}
                            helperText="Basic Auth Username oder API Key"
                        />

                        <TextField
                            label="LRS Password / Secret"
                            fullWidth
                            type="password"
                            value={content.lrsPassword || ''}
                            onChange={(e) => onChange({ ...content, lrsPassword: e.target.value })}
                            helperText="Basic Auth Password oder API Secret"
                        />

                        <TextField
                            label="Registration ID"
                            fullWidth
                            value={content.registrationId || ''}
                            onChange={(e) => onChange({ ...content, registrationId: e.target.value })}
                            helperText="UUID zur Identifizierung dieser spezifischen Registrierung (optional)"
                        />
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Accordion>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                    <Typography variant="h6">Tracking-Einstellungen</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Tracking Mode</InputLabel>
                            <Select
                                value={content.trackingMode || 'statements'}
                                label="Tracking Mode"
                                onChange={(e) => onChange({ ...content, trackingMode: e.target.value as any })}
                            >
                                <MenuItem value="statements">Statements Only</MenuItem>
                                <MenuItem value="state">State Only</MenuItem>
                                <MenuItem value="both">Statements & State</MenuItem>
                            </Select>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
                                Statements: Ereignisse verfolgen | State: Fortschritt speichern
                            </Typography>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Completion Verb</InputLabel>
                            <Select
                                value={content.completionVerb || 'http://adlnet.gov/expapi/verbs/completed'}
                                label="Completion Verb"
                                onChange={(e) => onChange({ ...content, completionVerb: e.target.value })}
                            >
                                {xapiVerbs.map((verb) => (
                                    <MenuItem key={verb.value} value={verb.value}>
                                        {verb.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
                                Verb, das anzeigt, dass die Aktivität abgeschlossen wurde
                            </Typography>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Success Verb</InputLabel>
                            <Select
                                value={content.successVerb || 'http://adlnet.gov/expapi/verbs/passed'}
                                label="Success Verb"
                                onChange={(e) => onChange({ ...content, successVerb: e.target.value })}
                            >
                                {xapiVerbs.map((verb) => (
                                    <MenuItem key={verb.value} value={verb.value}>
                                        {verb.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
                                Verb, das anzeigt, dass die Aktivität erfolgreich war
                            </Typography>
                        </FormControl>

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField
                                label="Min Score"
                                type="number"
                                value={content.minScore || 0}
                                onChange={(e) => onChange({ ...content, minScore: parseInt(e.target.value) || 0 })}
                                inputProps={{ min: 0 }}
                            />
                            <TextField
                                label="Max Score"
                                type="number"
                                value={content.maxScore || 100}
                                onChange={(e) => onChange({ ...content, maxScore: parseInt(e.target.value) || 100 })}
                                inputProps={{ min: 0 }}
                            />
                        </Box>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={content.requireCompletion !== false}
                                    onChange={(e) => onChange({ ...content, requireCompletion: e.target.checked })}
                                />
                            }
                            label="Completion erforderlich"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={content.requireSuccess === true}
                                    onChange={(e) => onChange({ ...content, requireSuccess: e.target.checked })}
                                />
                            }
                            label="Success erforderlich (zum Bestehen)"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={content.sendActor !== false}
                                    onChange={(e) => onChange({ ...content, sendActor: e.target.checked })}
                                />
                            }
                            label="Actor-Informationen senden"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={content.sendContext !== false}
                                    onChange={(e) => onChange({ ...content, sendContext: e.target.checked })}
                                />
                            }
                            label="Context-Informationen senden"
                        />
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Accordion>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CodeIcon />
                        <Typography variant="h6">xAPI Statement Beispiel</Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Alert severity="info">
                        <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
{`{
  "actor": {
    "name": "Lernender Name",
    "mbox": "mailto:learner@example.com"
  },
  "verb": {
    "id": "${content.completionVerb || 'http://adlnet.gov/expapi/verbs/completed'}",
    "display": { "de-DE": "abgeschlossen" }
  },
  "object": {
    "id": "${content.activityId || 'https://example.com/activities/lesson-1'}",
    "definition": {
      "name": { "de-DE": "${content.activityName || 'Activity Name'}" },
      "type": "${content.activityType || 'http://adlnet.gov/expapi/activities/lesson'}"
    }
  },
  "result": {
    "completion": true,
    "success": true,
    "score": {
      "min": ${content.minScore || 0},
      "max": ${content.maxScore || 100},
      "raw": 85
    }
  }
}`}
                        </Typography>
                    </Alert>
                </AccordionDetails>
            </Accordion>

            <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                    <strong>Über xAPI:</strong> xAPI (Experience API) ist ein E-Learning-Standard, der detaillierte
                    Tracking-Daten über Lernaktivitäten in einem Learning Record Store (LRS) speichert. Im Gegensatz
                    zu SCORM ist xAPI flexibler und kann Online- sowie Offline-Aktivitäten verfolgen.
                </Typography>
            </Alert>
        </Box>
    );
};

export default XAPIEditor;
